import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Organisation } from "../types";
import {
  createCheckoutSession,
  createPortalSession,
  getBillingUsage,
} from "../services/storageService";
import { ArrowRight, Check, Loader2, Zap } from "./Icons";
import { UpgradePrompt } from "./UpgradePrompt";

interface BillingTabProps {
  currentOrg: Organisation;
}

const tiers = [
  {
    id: "FREE",
    name: "Free",
    price: "£0",
    description: "Perfect for individuals and small projects",
    features: [
      "1 personal workspace",
      "5 taskplans",
      "10 runs per month",
      "7-day run history",
    ],
    highlighted: false,
  },
  {
    id: "TEAM",
    name: "Team",
    price: "£25",
    period: "/month",
    description: "For growing teams that need more power",
    features: [
      "Up to 30 members",
      "Unlimited teams",
      "Unlimited taskplans",
      "75 runs per month",
      "90-day run history",
      "GitHub Actions integration",
      "Role-based permissions",
    ],
    highlighted: true,
  },
  {
    id: "ENTERPRISE",
    name: "Enterprise",
    price: "£150",
    period: "/month",
    description: "For organisations with advanced needs",
    features: [
      "Everything in Team, plus:",
      "Up to 100 members",
      "Unlimited runs",
      "Unlimited run history",
      "Priority email support",
      "Audit logging",
    ],
    highlighted: false,
  },
];

const BillingTab: React.FC<BillingTabProps> = ({ currentOrg }) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [usage, setUsage] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const token = await getToken();
        const data = await getBillingUsage(currentOrg.id, token || undefined);
        setUsage(data);
      } catch (e) {
        console.error("Failed to fetch usage", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsage();
  }, [currentOrg.id, getToken]);

  const handleUpgrade = async (planId: string) => {
    if (planId === "FREE") return;
    setIsProcessing(planId);
    setError(null);
    try {
      const token = await getToken();
      const { url } = await createCheckoutSession(
        currentOrg.id,
        planId,
        token || undefined,
      );
      window.location.href = url;
    } catch (e: any) {
      setError(e.message);
      setIsProcessing(null);
    }
  };

  const handleManageBilling = async () => {
    setIsProcessing("MANAGE");
    setError(null);
    try {
      const token = await getToken();
      const { url } = await createPortalSession(
        currentOrg.id,
        token || undefined,
      );
      window.location.href = url;
    } catch (e: any) {
      setError(e.message);
      setIsProcessing(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  const isShared = currentOrg.type === "SHARED";
  const isPersonal =
    !isShared ||
    (currentOrg.name === "My Workspace" && currentOrg.memberIds?.length === 1);
  const isTrial = currentOrg.subscriptionStatus === "TRIALING";
  const isActive =
    currentOrg.subscriptionStatus === "ACTIVE" ||
    currentOrg.subscriptionStatus === "TRIALING";

  // Determine effective tier
  let currentTier: "FREE" | "TEAM" | "ENTERPRISE" | "EXPIRED" =
    currentOrg.tier || "FREE";
  if (isShared && !isActive) {
    currentTier = "EXPIRED";
  }

  // Trial calculations
  const trialDaysTotal = 14;
  const now = Date.now();
  const trialEnd = currentOrg.subscriptionCurrentPeriodEnd || 0;
  const trialStartedAt = trialEnd - trialDaysTotal * 24 * 60 * 60 * 1000;
  const trialDaysRemaining = Math.max(
    0,
    Math.ceil((trialEnd - now) / (24 * 60 * 60 * 1000)),
  );
  const trialProgress = Math.max(
    0,
    Math.min(100, ((now - trialStartedAt) / (trialEnd - trialStartedAt)) * 100),
  );
  const isTrialExpired = isTrial && trialDaysRemaining === 0;

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Current Subscription Summary */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm relative overflow-hidden">
        {isTrial && (
          <div className="absolute top-0 left-0 h-1 bg-slate-100 dark:bg-slate-800 w-full">
            <div
              className={`h-full transition-all duration-1000 ${isTrialExpired ? "bg-red-500" : "bg-indigo-600"}`}
              style={{ width: `${trialProgress}%` }}
            />
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                Current Plan
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                  currentTier === "ENTERPRISE"
                    ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                    : currentTier === "TEAM"
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                      : currentTier === "EXPIRED"
                        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                }`}
              >
                {currentTier === "EXPIRED" ? "Inactive" : currentTier}{" "}
                {isTrial ? "(Trial)" : ""}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              {currentTier === "FREE"
                ? "Free Tier"
                : currentTier === "TEAM"
                  ? "Team Plan"
                  : currentTier === "ENTERPRISE"
                    ? "Enterprise Plan"
                    : "Subscription Expired"}
            </h2>
            {isTrial ? (
              <p
                className={`text-sm mt-1 font-medium ${isTrialExpired ? "text-red-500" : "text-indigo-600 dark:text-indigo-400"}`}
              >
                {isTrialExpired
                  ? "Trial expired"
                  : `${trialDaysRemaining} days remaining in trial`}
              </p>
            ) : currentTier === "EXPIRED" ? (
              <p className="text-sm text-red-500 mt-1 font-medium">
                Your subscription has expired. Please update your billing
                details to regain access.
              </p>
            ) : (
              currentOrg.subscriptionCurrentPeriodEnd && (
                <p className="text-sm text-slate-500 mt-1">
                  Next billing date:{" "}
                  {new Date(
                    currentOrg.subscriptionCurrentPeriodEnd,
                  ).toLocaleDateString()}
                </p>
              )
            )}
          </div>

          {!isTrial && currentTier !== "FREE" && currentTier !== "EXPIRED" && (
            <button
              onClick={handleManageBilling}
              disabled={!!isProcessing}
              className="px-6 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-sm transition flex items-center justify-center gap-2"
            >
              {isProcessing === "MANAGE" ? (
                <Loader2 size={16} className="animate-spin" />
              ) : null}
              Manage Billing & Invoices
            </button>
          )}

          {isTrial && (
            <button
              onClick={() => handleUpgrade(currentTier)}
              disabled={!!isProcessing}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-500/20 font-bold text-sm transition flex items-center justify-center gap-2"
            >
              {isProcessing === currentTier ? (
                <Loader2 size={16} className="animate-spin" />
              ) : null}
              Activate Subscription
            </button>
          )}
        </div>

        {/* Usage Stats */}
        {usage && currentTier !== "EXPIRED" && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-slate-100 dark:border-slate-800">
            <div>
              <p className="text-sm text-slate-500 mb-1">Taskplans</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {usage.usage.plans}
                </span>
                <span className="text-slate-400 text-sm">
                  / {currentTier === "FREE" ? "5" : "∞"}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full"
                  style={{
                    width: `${Math.min(100, (usage.usage.plans / (currentTier === "FREE" ? 5 : usage.usage.plans)) * 100)}%`,
                  }}
                />
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Members</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {usage.usage.members}
                </span>
                <span className="text-slate-400 text-sm">
                  /{" "}
                  {currentTier === "FREE"
                    ? "1"
                    : currentTier === "TEAM"
                      ? "30"
                      : "100"}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full"
                  style={{
                    width: `${Math.min(100, (usage.usage.members / (currentTier === "FREE" ? 1 : currentTier === "TEAM" ? 30 : 100)) * 100)}%`,
                  }}
                />
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Runs (Last 30 days)</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {usage.usage.runsThisMonth}
                </span>
                <span className="text-slate-400 text-sm">
                  /{" "}
                  {currentTier === "FREE"
                    ? "50"
                    : currentTier === "TEAM"
                      ? "75"
                      : "∞"}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full"
                  style={{
                    width: `${Math.min(100, (usage.usage.runsThisMonth / (currentTier === "FREE" ? 50 : currentTier === "TEAM" ? 75 : usage.usage.runsThisMonth)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Pricing Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
        {tiers.map((tier) => {
          const isHighlighted = tier.id === (currentOrg.tier || "FREE");
          const isCurrent = currentTier === tier.id;
          const isUpgradable =
            (!isCurrent || currentTier === "EXPIRED") &&
            tier.id !== "FREE" &&
            (currentTier === "FREE" ||
              currentTier === "EXPIRED" ||
              (currentTier === "TEAM" && tier.id === "ENTERPRISE") ||
              (isTrial && currentTier === "ENTERPRISE" && tier.id === "TEAM"));

          // Show CTA if:
          // 1. It's the highlighted plan (to show "Current Plan" or "Expired")
          // 2. It's NOT the free tier and we are in a shared workspace
          const showCTA = tier.id !== "FREE" && (isHighlighted || isShared);

          return (
            <div
              key={tier.id}
              className={`relative rounded-2xl p-6 flex flex-col w-full ${
                tier.highlighted && !isHighlighted
                  ? "bg-slate-950 text-white ring-2 ring-indigo-600"
                  : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
              } ${isHighlighted ? (currentTier === "EXPIRED" ? "ring-2 ring-red-500 shadow-lg scale-[1.02]" : "ring-2 ring-indigo-500 shadow-lg scale-[1.02]") : ""}`}
            >
              {isHighlighted && currentTier !== "FREE" && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold uppercase py-1 px-3 rounded-full shadow-lg ${currentTier === "EXPIRED" ? "bg-red-500" : "bg-indigo-500"}`}
                >
                  {currentTier === "EXPIRED" ? "Expired" : "Active Plan"}
                </div>
              )}
              <h3
                className={`font-bold ${tier.highlighted && !isHighlighted ? "text-white" : "text-slate-900 dark:text-white"}`}
              >
                {tier.name}
              </h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span
                  className={`text-3xl font-bold ${tier.highlighted && !isCurrent ? "text-white" : "text-slate-900 dark:text-white"}`}
                >
                  {tier.price}
                </span>
                {tier.period && (
                  <span className="text-xs text-slate-500">{tier.period}</span>
                )}
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs">
                    <Check
                      className={`h-4 w-4 shrink-0 ${tier.highlighted && !isCurrent ? "text-indigo-400" : "text-green-500"}`}
                    />
                    <span
                      className={
                        tier.highlighted && !isCurrent
                          ? "text-slate-300"
                          : "text-slate-600 dark:text-slate-400"
                      }
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              {showCTA && (
                <button
                  disabled={!isUpgradable || !!isProcessing}
                  onClick={() => handleUpgrade(tier.id)}
                  className={`mt-8 w-full py-2.5 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2 ${
                    isCurrent
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-default"
                      : tier.highlighted
                        ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                        : "bg-slate-900 dark:bg-white dark:text-slate-900 text-white hover:bg-slate-800 dark:hover:bg-slate-100 transition"
                  }`}
                >
                  {isProcessing === tier.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : null}
                  {isCurrent
                    ? "Current Plan"
                    : isUpgradable
                      ? isTrial &&
                        currentTier === "ENTERPRISE" &&
                        tier.id === "TEAM"
                        ? "Downgrade to Team"
                        : `Upgrade to ${tier.name}`
                      : "Contact Support"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BillingTab;
